// SperrWert.tsx — der Wert eines gesperrten Feldes als markierbarer Text (T-62).
//
// Die Kontaktakte sperrt ihren Reiter „Allgemein" mit `<fieldset disabled>`
// (Schloss zu, oder kein Schreibrecht). Deaktivierte Felder lassen sich in
// Chrome/Edge aber weder markieren noch kopieren — wer nur die IBAN oder die
// Telefonnummer übernehmen will, muss erst das Schloss öffnen.
//
// Warum nicht `readOnly` statt `disabled`? Das `fieldset disabled` ist das
// Sicherheitsnetz der Akte (AkteAllgemeinAnlage): Es sperrt JEDES Feld darin,
// auch eines, das ein Widget ohne `readOnly`-Durchreichung rendert. Das bleibt
// unverändert.
//
// Stattdessen steht dieser Baustein direkt HINTER dem Feld und legt dessen Wert
// als normalen Text darüber — sichtbar nur per CSS, wenn ein Vorfahre
// `fieldset:disabled` ist (contactRedesign.css, `.nk-sperrwert`). Kein Context,
// keine Prop-Kette; fehlt der Baustein an einem Widget, bleibt es wie bisher.
//
// Lage, Schrift und Innenabstand übernimmt er vom Feld davor (gemessen), damit
// der Text exakt dort liegt, wo er im Feld stünde. Bezug der Lage ist das
// ELTERNELEMENT des Bausteins (per CSS `position: relative`, solange er Text
// zeigt) — also eine Box, die mit dem Feld im selben Scroll-Container sitzt.
// Vorher war es irgendein positionierter Vorfahre oberhalb des Akten-Scrollers:
// Beim Scrollen wanderte das Feld, der Text blieb stehen (Anrede „Herr" stand
// zwischen Anrede und Nachname). Deshalb auch kein `getBoundingClientRect`
// (Viewport-Lage, ändert sich mit jedem Scrollen) und kein `position: fixed`. Ohne `wert` liest er den
// ANGEZEIGTEN Wert aus dem Feld selbst — so erscheint die IBAN in 4er-Gruppen
// und die Telefonnummer formatiert, ohne dass jedes Widget das nachbauen muss.
//
// Nur Anzeige: kein `contentEditable`, kein `onChange` — die Sperre bleibt dicht.
// `aria-hidden`, weil der Screenreader den Wert schon am (deaktivierten) Feld liest.

import * as React from "react";

type Feld = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const istFeld = (el: Element | null): el is Feld =>
  !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");

/** Das Feld vor dem Baustein — direkt oder als letztes Feld im Element davor (Widget-Hülle). */
export function feldVor(el: Element | null): Feld | null {
  const vor = el?.previousElementSibling ?? null;
  if (!vor) return null;
  if (istFeld(vor)) return vor;
  const innen = vor.querySelectorAll("input, textarea, select");
  const letztes = innen.length ? innen[innen.length - 1] : null;
  return istFeld(letztes) ? letztes : null;
}

/** `2026-09-01` → `01.09.2026`; alles andere unverändert. */
export const datumText = (wert: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wert.trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : wert;
};

/** Was das Feld gerade anzeigt — bei einer Auswahl das Label, beim Datum deutsch. */
export function angezeigterWert(feld: Feld): string {
  if (feld.tagName === "SELECT") {
    const sel = feld as HTMLSelectElement;
    if (!sel.value) return "";
    const opt = sel.options[sel.selectedIndex];
    return opt ? opt.text : sel.value;
  }
  const input = feld as HTMLInputElement;
  if (input.type === "checkbox" || input.type === "radio" || input.type === "hidden" || input.type === "file") return "";
  return input.type === "date" ? datumText(input.value) : input.value;
}

export type SperrWertProps = {
  /** Fester Text; ohne ihn wird der angezeigte Wert des Feldes davor gelesen. */
  wert?: string | null;
  /** `datum`: `JJJJ-MM-TT` wird als `TT.MM.JJJJ` gezeigt. */
  art?: "text" | "datum" | "auswahl";
  /** Für `art="auswahl"` mit festem `wert`: das Label der gewählten Option. */
  optionen?: ReadonlyArray<{ value: string; label: string }>;
  /** Mehrzeilig (Textarea): Umbrüche bleiben, der Text scrollt im Feld. */
  mehrzeilig?: boolean;
};

function textAus(p: SperrWertProps): string | null {
  if (p.wert == null) return null;
  const w = String(p.wert);
  if (p.art === "datum") return datumText(w);
  if (p.art === "auswahl") return w ? (p.optionen?.find((o) => o.value === w)?.label ?? w) : "";
  return w;
}

/**
 * Versatz des Feldes zur Bezugsbox — über die `offsetParent`-Kette, damit auch
 * ein Feld in einer (selbst positionierten) Widget-Hülle stimmt. Layout-Maße,
 * keine Viewport-Maße: Scrollen und Transformationen (Fenster) ändern sie nicht.
 */
export function versatzZu(feld: HTMLElement, bezug: Element | null): { left: number; top: number } {
  let left = 0;
  let top = 0;
  let el: HTMLElement | null = feld;
  while (el && el !== bezug) {
    left += el.offsetLeft;
    top += el.offsetTop;
    const op = el.offsetParent as HTMLElement | null;
    if (op && op !== bezug) {
      left += op.clientLeft;
      top += op.clientTop;
    }
    el = op;
  }
  return { left, top };
}

/** Übernimmt Box und Schrift des Feldes — Rahmen zählt zum Innenabstand. */
function lageVon(feld: Feld, bezug: Element | null): React.CSSProperties {
  const cs = window.getComputedStyle(feld);
  const px = (v: string) => parseFloat(v) || 0;
  const { left, top } = versatzZu(feld, bezug);
  const oben = px(cs.paddingTop) + px(cs.borderTopWidth);
  const unten = px(cs.paddingBottom) + px(cs.borderBottomWidth);
  const mehrzeilig = feld.tagName === "TEXTAREA";
  return {
    left,
    top,
    width: feld.offsetWidth,
    height: feld.offsetHeight,
    paddingTop: oben,
    paddingRight: px(cs.paddingRight) + px(cs.borderRightWidth),
    paddingBottom: unten,
    paddingLeft: px(cs.paddingLeft) + px(cs.borderLeftWidth),
    fontFamily: cs.fontFamily,
    fontSize: cs.fontSize,
    fontWeight: cs.fontWeight as React.CSSProperties["fontWeight"],
    letterSpacing: cs.letterSpacing,
    // Einzeilig steht der Text im Feld senkrecht mittig — die Zeilenhöhe füllt
    // deshalb den Inhaltsbereich, sonst klebt der Text oben im Feld.
    lineHeight: mehrzeilig ? cs.lineHeight : `${Math.max(0, feld.offsetHeight - oben - unten)}px`,
    // Mehrzeilig wie die Textarea: Umbrüche bleiben, der Text scrollt im Feld.
    ...(mehrzeilig ? { whiteSpace: "pre-wrap" as const, overflow: "auto" as const } : {}),
    textAlign: cs.textAlign as React.CSSProperties["textAlign"],
  };
}

/** Die Box, an der der Text hängt: das Elternelement (CSS macht es zum Bezug). */
const bezugVon = (el: HTMLElement | null): Element | null => el?.offsetParent ?? el?.parentElement ?? null;

export function SperrWert(props: SperrWertProps) {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const fest = textAus(props);
  const [gelesen, setGelesen] = React.useState("");
  const [lage, setLage] = React.useState<React.CSSProperties | null>(null);
  // Nur in einer gesperrten Maske steht überhaupt Text im Element — entsperrt
  // gäbe es den Wert sonst zweimal im DOM (Suche im Browser, Tests per Text).
  const [gesperrt, setGesperrt] = React.useState(false);
  const text = gesperrt ? (fest ?? gelesen) : "";

  // Bei jedem Rendern nachlesen: Der Wert des Feldes ändert sich mit dem
  // Formularzustand, und genau dann rendert auch dieser Baustein neu.
  React.useLayoutEffect(() => {
    const feld = feldVor(ref.current);
    const zu = !!ref.current?.closest("fieldset[disabled]");
    setGesperrt(zu);
    if (!feld || !zu) return;
    if (fest == null) {
      const w = angezeigterWert(feld);
      setGelesen((alt) => (alt === w ? alt : w));
    }
    const neu = lageVon(feld, bezugVon(ref.current));
    setLage((alt) => (alt && JSON.stringify(alt) === JSON.stringify(neu) ? alt : neu));
  });

  // Größe ändert sich ohne Rendern (Fensterbreite, Karte aufgeklappt), und das
  // Schloss kann das fieldset umschalten, ohne dass dieser Baustein neu rendert.
  React.useEffect(() => {
    const el = ref.current;
    const feld = feldVor(el);
    if (!el || !feld) return;
    const pruefe = () => {
      const zu = !!el.closest("fieldset[disabled]");
      setGesperrt(zu);
      if (zu) {
        setLage(lageVon(feld, bezugVon(el)));
        if (fest == null) setGesperrtGelesen(angezeigterWert(feld));
      }
    };
    const setGesperrtGelesen = (w: string) => setGelesen((alt) => (alt === w ? alt : w));
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(pruefe);
    ro?.observe(feld);
    const mo = typeof MutationObserver === "undefined" ? null : new MutationObserver(pruefe);
    for (let fs = el.closest("fieldset"); fs; fs = fs.parentElement?.closest("fieldset") ?? null) {
      mo?.observe(fs, { attributes: true, attributeFilter: ["disabled"] });
    }
    return () => {
      ro?.disconnect();
      mo?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leerer Wert: nichts darüberlegen — der Platzhalter am Feld bleibt sichtbar.
  // Das (leere) Element bleibt trotzdem stehen, damit es das Feld beim nächsten
  // Wert wiederfindet.
  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={"nk-sperrwert" + (props.mehrzeilig ? " mehrzeilig" : "") + (text ? "" : " leer")}
      style={lage ?? undefined}
    >
      {text}
    </span>
  );
}

export default SperrWert;
